/**
 * Unregisters the legacy Partytown service worker and its caches left in returning
 * visitors' browsers after Partytown was removed from the build.
 * Client-only, never throws, and only touches Partytown-scoped workers. Persists a
 * one-time flag so it doesn't re-run for the vast majority of visitors who never had it.
 */
const CLEANUP_DONE_KEY = 'partytown-cleanup-done'

export const usePartytownCleanup = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
    return

  try {
    if (localStorage.getItem(CLEANUP_DONE_KEY)) return
  } catch {
    // localStorage unavailable (private mode): run cleanup anyway, just uncached.
  }

  const isPartytown = (value?: string | null) =>
    !!value && value.includes('partytown')

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        .filter(
          registration =>
            isPartytown(registration.scope) ||
            isPartytown(registration.active?.scriptURL) ||
            isPartytown(registration.installing?.scriptURL) ||
            isPartytown(registration.waiting?.scriptURL)
        )
        .map(registration => registration.unregister())
    )

    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      await Promise.all(
        cacheKeys.filter(isPartytown).map(key => caches.delete(key))
      )
    }

    try {
      localStorage.setItem(CLEANUP_DONE_KEY, '1')
    } catch {
      // ignore: cleanup succeeded, it will just re-run next mount
    }
  } catch (error) {
    console.error('Partytown cleanup failed', error)
  }
}
